import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import { websocketActionsWorkspaces } from '@/common/context/websocketActions/constants'
import useWebsocketActions from '@/common/hooks/useWebsocketActions/useWebsocketActions'
import returnsServices from '@/returns/services/services'

import { pendingWebsocketActionTypes } from '../useWebsocketTransactionAction/constants'
import { getAddressPayload, getSendInstructionsPayload } from './helpers'

const useShippingDetails = ({ data, fetchData, setToastInfo, onRetryLabel }) => {
  const [showEditAddressModal, setShowEditAddressModal] = useState(false)
  const [retryLabelAfterEditAddress, setRetryLabelAfterEditAddress] = useState(false)
  const [showTrackingNumberModal, setShowTrackingNumberModal] = useState(false)
  const [showChangeDeliveryMethodDrawer, setShowChangeDeliveryMethodDrawer] = useState(false)
  const [showSendInstructionsModal, setShowInstructionsModal] = useState(false)
  const { t } = useTranslation()
  const { setPendingWebsocketAction } = useWebsocketActions(websocketActionsWorkspaces.transactions)

  const { fetchData: editAddress, loading: editingAddress } = useFetch(returnsServices.editReturnAddress, {
    doInitialRequest: false,
  })

  const { fetchData: saveTrackingNumber, loading: savingTrackingNumber } = useFetch(
    returnsServices.saveTrackingNumber,
    {
      doInitialRequest: false,
    }
  )

  const { fetchData: changeDeliveryMethod, loading: changingDeliveryMethod } = useFetch(
    returnsServices.changeDeliveryMethod,
    {
      doInitialRequest: false,
    }
  )

  const { fetchData: sendInstructions, loading: sendingInstructions } = useFetch(returnsServices.sendInstructions, {
    doInitialRequest: false,
  })

  const onEditAddress = async address => {
    try {
      const addressPayload = getAddressPayload(address, data?.shipping?.delivery_method)
      await editAddress({ returnId: data.id, address: addressPayload })
      closeEditAddressModal()

      if (retryLabelAfterEditAddress) await onRetryLabel()
      else fetchData()
    } catch (err) {
      setToastInfo({ show: true, message: err.errorMessage, type: 'error' })
    }
  }

  const onChangeDeliveryMethod = async payload => {
    try {
      const response = await changeDeliveryMethod({ returnId: data.id, payload })

      setShowChangeDeliveryMethodDrawer(false)
      setPendingWebsocketAction(pendingWebsocketActionTypes.changeDeliveryMethod, response)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onSendInstructions = async ({ returnLabels, invoices, customsDeclaration }) => {
    try {
      const payload = getSendInstructionsPayload({ returnLabels, invoices, transaction: data, customsDeclaration })
      await sendInstructions({ returnId: data.id, payload })
      setShowInstructionsModal(false)
      setToastInfo({ show: true, message: t('Instrucciones enviadas correctamente'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const openTrackingNumberModal = () => {
    setShowTrackingNumberModal(true)
  }

  const closeTrackingNumberModal = () => {
    setShowTrackingNumberModal(false)
  }

  const openEditAddressModal = () => {
    setShowEditAddressModal(true)
  }

  const openEditAddressAndRetryLabelModal = () => {
    setRetryLabelAfterEditAddress(true)
    setShowEditAddressModal(true)
  }

  const closeEditAddressModal = () => {
    setShowEditAddressModal(false)
    setRetryLabelAfterEditAddress(false)
  }

  const openChangeDeliveryMethodDrawer = () => {
    setShowChangeDeliveryMethodDrawer(true)
  }

  const closeChangeDeliveryMethodDrawer = () => {
    setShowChangeDeliveryMethodDrawer(false)
  }

  const openSendInstructionsModal = () => {
    setShowInstructionsModal(true)
  }

  const closeSendInstructionsModal = () => {
    setShowInstructionsModal(false)
  }

  return {
    showEditAddressModal,
    openEditAddressModal,
    openEditAddressAndRetryLabelModal,
    closeEditAddressModal,
    retryLabelAfterEditAddress,
    onEditAddress,
    editingAddress,
    openTrackingNumberModal,
    closeTrackingNumberModal,
    showTrackingNumberModal,
    saveTrackingNumber,
    savingTrackingNumber,
    showChangeDeliveryMethodDrawer,
    openChangeDeliveryMethodDrawer,
    closeChangeDeliveryMethodDrawer,
    onChangeDeliveryMethod,
    changingDeliveryMethod,
    showSendInstructionsModal,
    openSendInstructionsModal,
    closeSendInstructionsModal,
    sendInstructions,
    sendingInstructions,
    onSendInstructions,
  }
}

export default useShippingDetails
